import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Header from "./Front/Header";
import Footer from "./Front/Footer";
import API_BASE_URL from "../Config";
import { v4 as uuidv4 } from "uuid";
import parse from "html-react-parser";
import DOMPurify from "dompurify";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Thumbs } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/thumbs";

import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";

import { toast } from "react-toastify";
import "../styles/global.css";
import { isDoctorPublic } from "../utils/doctorVisibility";
import { getDoctorReviewsWithSamples, getDoctorReviewerAvatarUrl, DEFAULT_AVATAR, DEFAULT_AVATAR_FALLBACK } from "../utils/doctorReviews";

/* Normalizes doctor.treatments into a clean string[] of display labels,
   no matter which shape the backend sends it in:
     - a string:  "Obesity|Skin Conditions (Eczema, Psoriasis, and allergy skin)"
                  or the legacy "Obesity,Skin Conditions..." comma format
     - an array of plain strings:  ["Obesity", "Skin Conditions..."]
     - an array of objects:  [{ name: "Obesity" }] / [{ label: "Obesity" }] / [{ value: "Obesity" }]
   This avoids the earlier bug where treatment names containing commas
   (e.g. "Skin Conditions (Eczema, Psoriasis, and allergy skin)") got torn
   into multiple broken lines by a naive .split(","). */
const getTreatmentList = (treatments, options = []) => {
  if (!treatments) return [];

  const resolve = (raw) => {
    const key = String(raw).trim();
    if (!key) return "";
    const match = options.find((o) => String(o.value) === key || o.label === key);
    return match ? match.label : key;
  };

  if (Array.isArray(treatments)) {
    return treatments
      .map((t) => {
        if (t == null) return "";
        if (typeof t === "string") return resolve(t);
        if (typeof t === "object") return resolve(t.name ?? t.label ?? t.value ?? "");
        return resolve(t);
      })
      .filter(Boolean);
  }

  if (typeof treatments === "string") {
    const raw = treatments.trim();
    if (!raw) return [];
    // Pipe is the safe separator (used going forward). For legacy
    // comma-separated records, split on commas that are NOT inside
    // parentheses, so "Skin Conditions (Eczema, Psoriasis, and
    // allergy skin)" stays a single item instead of being torn apart.
    let parts;
    if (raw.includes("|")) {
      parts = raw.split("|");
    } else {
      parts = [];
      let depth = 0;
      let current = "";
      for (const ch of raw) {
        if (ch === "(") depth++;
        if (ch === ")") depth = Math.max(0, depth - 1);
        if (ch === "," && depth === 0) {
          parts.push(current);
          current = "";
        } else {
          current += ch;
        }
      }
      if (current) parts.push(current);
    }
    return parts.map((p) => resolve(p)).filter(Boolean);
  }

  return [];
};

/* doctor.documents is expected to be a JSON string like
   '[{"name":"MBBS Certificate"}, ...]'. Backend data can be messy
   (empty string, malformed JSON, already-an-array, etc.), so this
   never throws — it just returns [] when it can't make sense of it. */
const getDocumentList = (documents) => {
  if (!documents) return [];
  if (Array.isArray(documents)) return documents;
  if (typeof documents === "string") {
    try {
      const parsed = JSON.parse(documents);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.log("Failed to parse doctor.documents:", e);
      return [];
    }
  }
  return [];
};

/* ─────────────────────────────────────────────────────────────────
   InfoTabs — a single, self-contained tab widget.
   Tab switching is driven by React state (useState), NOT Bootstrap's
   data-bs-toggle="pill" JS. This matters because this component is
   rendered TWICE on the page (once for desktop, once for mobile,
   toggled with d-lg-block/d-none classes). Bootstrap's tab JS looks
   panes up by `id`, and two instances with identical ids on the page
   made it grab the wrong (often hidden) pane — that was the root
   cause of Reviews/Treatment/Certification not switching properly.
   Each InfoTabs instance keeps its own independent state, so there's
   no id collision and no cross-talk between the desktop and mobile
   copies.
   ───────────────────────────────────────────────────────────────── */
const InfoTabs = ({ doctor, treatmentOptions, renderDoctorReviewsPane, showHealthfeed }) => {
  const [activeTab, setActiveTab] = useState("info");

  const treatmentList = getTreatmentList(doctor?.treatments, treatmentOptions);
  const documentList = getDocumentList(doctor?.documents);

  const tabs = [
    { key: "info", label: "Info" },
    { key: "certification", label: "Certification" },
    { key: "treatment", label: "Treatment" },
    { key: "reviews", label: "Reviews" },
    ...(showHealthfeed ? [{ key: "healthfeed", label: "Healthfeed" }] : []),
  ];

  return (
    <div className="tab-section-1">
      <ul className="nav nav-pills mb-3 nav-tabs" role="tablist">
        {tabs.map((tab) => (
          <li className="nav-item" role="presentation" key={tab.key}>
            <button
              type="button"
              className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
              role="tab"
              aria-selected={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      <div className="tab-content">
        {/* Info */}
        {activeTab === "info" && (
          <div className="tab-pane fade show active" role="tabpanel">
            <div className="dd-clinic-block">
              <div className="doctor-location-1 mb-3 fw-bold">{doctor?.clinics}</div>
              <div className="d-flex gap-3 flex-wrap">
                <div className="w-1"><span>{doctor?.address}</span></div>
                <div className="w-1">
                  <span>₹ {doctor?.videocall_price}</span>
                  <span> · Verified details</span>
                </div>
              </div>

              {doctor?.address && (
                <a
                  className="direction"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(doctor.address || "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fa-solid fa-location-arrow"></i> Get Directions
                </a>
              )}

              <p>{doctor?.desc}</p>

              <div className="thumb-1">
                {doctor?.clinicImage &&
                  doctor.clinicImage
                    .split(",")
                    .filter(Boolean)
                    .map((img, index) => (
                      <a
                        className="thumb-2"
                        key={index}
                        href={`${API_BASE_URL}public/clinic/${img}`}
                        data-fancybox="gallery"
                      >
                        <img
                          src={`${API_BASE_URL}public/clinic/${img}`}
                          alt={`image-${index}`}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/no-image.png";
                          }}
                        />
                      </a>
                    ))}
              </div>
            </div>
          </div>
        )}

        {/* Certification */}
        {activeTab === "certification" && (
          <div className="tab-pane fade show active" role="tabpanel">
            <div className="qa-container">
              <div className="qa-card">
                {documentList.length > 0 ? (
                  <ul>
                    {documentList.map((doc, index) => (
                      <li className="mb-3" key={index}>{doc?.name || "Untitled document"}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted mb-0">No certifications listed for this doctor.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Treatment */}
        {activeTab === "treatment" && (
          <div className="tab-pane fade show active" role="tabpanel">
            <div className="qa-container">
              <div className="qa-card">
                {treatmentList.length > 0 ? (
                  <ul>
                    {treatmentList.map((item, index) => (
                      <li className="mb-3" key={index}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted mb-0">No treatments listed for this doctor.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Reviews */}
        {activeTab === "reviews" && (
          <div className="tab-pane fade show active" role="tabpanel">
            {renderDoctorReviewsPane()}
          </div>
        )}

        {/* Healthfeed (mobile only, matches original design) */}
        {activeTab === "healthfeed" && (
          <div className="tab-pane fade show active" role="tabpanel">
            <div className="bg-white shadow rounded-5 p-3 mb-3">
              <div className="d-flex align-items-center justify-content-center flex-column gap-2">
                <img src="./assets/images/pencil-clipboard-v1.svg" alt="" />
                <div className="dc-book-btn">Read all articles</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FrontDoctorDetails = () => {
  const [visitType, setVisitType] = useState("video");
  const [descExpanded, setDescExpanded] = useState(false);

  const navigate = useNavigate();
  const { slug } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [imagesSwip, setImages] = useState(null);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [doctorAvialablity, setDoctorAvialablity] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slots, setSlots] = useState({ morning: [], afternoon: [], evening: [] });
  const [activeDate, setActiveDate] = useState("");
  const [treatmentOptions, setTreatmentOptions] = useState([]);
  const [doctorReviews, setDoctorReviews] = useState([]);
  const [doctorReviewStats, setDoctorReviewStats] = useState({ average_rating: 0, total_reviews: 0 });

  /* The doctor record only stores treatment IDs (confirmed by
     DoctorEdit.js's toSelected pattern: options.filter(opt =>
     ids.includes(String(opt.value)))) — not names. So we fetch the
     master treatment list once here and resolve IDs -> names below,
     exactly like the admin edit page does. */
  useEffect(() => {
    fetch(`${API_BASE_URL}api/treatments_front`)
      .then((r) => r.json())
      .then((data) => {
        if (data?.status && Array.isArray(data.data)) {
          setTreatmentOptions(data.data.map((d) => ({ value: String(d.id), label: d.name })));
        }
      })
      .catch((e) => console.log("treatments_front:", e));
  }, []);

  // Initialize Fancybox
  useEffect(() => {
    Fancybox.bind('[data-fancybox="gallery"]', {
      Thumbs: { autoStart: true },
      Toolbar: {
        display: {
          left: ["infobar"],
          middle: ["zoomIn", "zoomOut", "toggle1to1", "rotateCCW", "rotateCW", "flipX", "flipY"],
          right: ["slideshow", "thumbs", "close"],
        },
      },
    });
    return () => { Fancybox.destroy(); };
  }, []);

  const getNextDays = () => {
    const days = [];

    for (let i = 0; i < 4; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);

      const formattedDate = date.toLocaleDateString("en-CA", {
        timeZone: "Asia/Kolkata"
      }); // YYYY-MM-DD format

      let label = date.toLocaleDateString("en-IN", {
        timeZone: "Asia/Kolkata",
        weekday: "short",
        day: "numeric",
        month: "short"
      });

      if (i === 0) label = "Today";
      if (i === 1) label = "Tomorrow";

      days.push({ label, date: formattedDate });
    }

    return days;
  };

  const handleSlotClick = (slot) => {
    if (!slot.available) return;
    setSelectedSlot(slot.time + slot.AM_PM);
  };

  const days = getNextDays();

  const fetchDoctor = async (slug) => {
    const response = await fetch(`${API_BASE_URL}api/doctor/${slug}`);
    const json = await response.json();
    if (json.status) {
      setDoctor(json.data);
      const images = json.data.images ? json.data.images.split(",") : [];
      setImages(images);
    }
  };

  const fetchDoctorAvailability = async (slug, date) => {
    try {
      const response = await fetch(`${API_BASE_URL}api/doctorAvailability/${slug}?date=${date}`);
      const json = await response.json();
      setDoctorAvialablity(json);
      setSlots(json.slots);
    } catch (error) {
      console.error("Error fetching doctor availability:", error);
    }
  };

  const handleDateClick = (date) => {
    setActiveDate(date);
    fetchDoctorAvailability(slug, date);
  };

  const renderDoctorReviewsPane = () => (
    <div className="qa-container">
      {getDoctorReviewsWithSamples(doctor, doctorReviews).length > 0 ? (
        getDoctorReviewsWithSamples(doctor, doctorReviews).map((r) => (
          <div className="qa-card" key={r.id}>
            <div className="qa-header">
              <div className="qa-avatar"><img src={getDoctorReviewerAvatarUrl(r, API_BASE_URL)} alt="avatar" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = DEFAULT_AVATAR_FALLBACK; }} /></div>
              <div className="qa-content">
                <h3 className="qa-title">{r.reviewer}</h3>
                <div className="rating mb-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <i key={n} className={n <= r.rating ? "fa-solid fa-star" : "fa-regular fa-star"}></i>
                  ))}
                </div>
                <p className="qa-description">{r.comment || "No written feedback provided."}</p>
                <div className="qa-meta">
                  <span>{r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN") : ""}</span>
                </div>
              </div>
            </div>
          </div>
        ))
      ) : (
        <p className="text-muted mb-0">No reviews yet for this doctor.</p>
      )}
    </div>
  );

  useEffect(() => {
    fetchDoctor(slug);
    const today = new Date().toISOString().split("T")[0];
    setActiveDate(today);
    fetchDoctorAvailability(slug, today);
  }, [slug]);

  // Only reviews an admin has approved (status = Active) come back from this endpoint.
  useEffect(() => {
    if (!doctor?.id) { setDoctorReviews([]); setDoctorReviewStats({ average_rating: 0, total_reviews: 0 }); return; }
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}api/doctor-reviews/${doctor.id}`);
        const data = await res.json();
        if (data?.status) {
          setDoctorReviews(data.data || []);
          setDoctorReviewStats({ average_rating: data.average_rating || 0, total_reviews: data.total_reviews || 0 });
        }
      } catch (err) {
        console.log("doctor reviews fetch error:", err);
      }
    })();
  }, [doctor?.id]);

  const handleCallNow = () => {
    if (!selectedSlot) {
      toast.error(" Please select a slot");
      return;
    }
    let fees = doctor.consult_price;
    if (visitType === 'video') {
      fees = doctor.videocall_price;
    }
    navigate("/doctor-booking", {
      state: {
        doctor_id: doctor.id,
        doctor_slug: doctor.slug,
        booking_date: activeDate,
        booking_time: selectedSlot,
        fees: fees,
        visitType: visitType
      }
    });
  };

  let deviceId = localStorage.getItem("device_id");
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem("device_id", deviceId);
  }

  const currentFee = visitType === 'video' ? doctor?.videocall_price : doctor?.consult_price;

  /* ── Slot renderer helper ── */
  const renderSlots = (label, icon, slotArray) => {
    if (!slotArray || slotArray.length === 0) return null;
    return (
      <li>
        <div className="d-flex flex-wrap gap-2">
          <div className="slot-time d-flex align-items-center text-dark">
            <i className={icon}></i> {label}
          </div>
          <div className="d-flex flex-wrap gap-3">
            {slotArray.map((slot, i) => {
              const slotKey = slot.time + slot.AM_PM;
              return (
                <button key={i} onClick={() => handleSlotClick(slot)} disabled={!slot.available}
                  className={`badge badge-slot ${!slot.available ? "bg-warning" : ""} ${selectedSlot === slotKey ? "bg-primary text-white" : ""}`}>
                  {slot.time} {slot.AM_PM}
                </button>
              );
            })}
          </div>
        </div>
      </li>
    );
  };

  /* ── Appointment panel helper ── */
  const renderAppointment = (priceField, title, icon) => (
    <div className="appointment-card">
      <div className="appointment-header">
        <div className="left">
          <div className="icon"><i className={icon}></i></div>
          <span className="title">{title}</span>
        </div>
        <div className="fee">₹ {doctor?.[priceField]}</div>
      </div>
      <div className="appointment-body">
        <h4>{doctor?.clinics}</h4>
        <div className="rating">
          <span className="text-dark">₹{doctor?.[priceField]}</span>
          <a href="#" onClick={(e) => e.preventDefault()}>Verified details</a>
        </div>
        <p className="location">{doctor?.address}</p>
      </div>
      <div className="content-section mt-3 w-100 tab-section-1 p-3">
        <nav>
          <div className="nav nav-tabs w-100 slot-tabs" role="tablist">
            {days.map((day, index) => (
              <button key={index} className={`nav-link ${activeDate === day.date ? "active" : ""}`}
                onClick={() => handleDateClick(day.date)}>
                {day.label}
              </button>
            ))}
          </div>
        </nav>
        <div className="tab-content">
          <div className="tab-pane fade show active" role="tabpanel">
            <ul className="schedule-list">
              <p>{doctorAvialablity?.total_available_slots} slots Available</p>
              {renderSlots("Morning", "fa-solid fa-cloud-sun", slots?.morning)}
              {renderSlots("Afternoon", "fa-solid fa-sun", slots?.afternoon)}
              {renderSlots("Evening", "fa-solid fa-cloud-moon", slots?.evening)}
              {(!slots?.morning?.length && !slots?.afternoon?.length && !slots?.evening?.length) && (
                <div className="dd-no-slots">
                  <i className="fa-regular fa-calendar-xmark" style={{ fontSize: '1.5rem', display: 'block', marginBottom: 8 }}></i>
                  No slots available for this date
                </div>
              )}
            </ul>
          </div>
        </div>
      </div>
      <div className="no-slot">
        <div className="d-flex gap-2 flex-wrap flex-md-nowrap">
          <button onClick={handleCallNow}
            className="dc-book-btn d-flex w-100 align-items-center justify-content-center">
            <span><i className="bi bi-telephone"></i> Book Now – ₹{doctor?.[priceField]}</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Guards direct links to a doctor's page (e.g. bookmarked or shared URLs)
  // that an admin has since turned Inactive or un-approved — otherwise the
  // full profile and booking flow would still be reachable even though the
  // doctor no longer shows up in any listing.
  if (doctor && !isDoctorPublic(doctor)) {
    return (
      <>
        <Header />
        <div className="doctor-detail-page">
          <div className="container-fluid py-5 text-center">
            <h3 className="mb-2">This doctor is currently unavailable</h3>
            <p className="text-muted mb-4">
              This profile has been temporarily taken offline by the clinic.
            </p>
            <Link to="/doctor-list-page" className="btn btn-main btn-css-1 book-btn rounded-pill">
              Browse other doctors
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />

      <div className="doctor-detail-page">

        {/* Breadcrumb */}
        <div className="cl-breadcrumb-bar py-xl-3 py-3">
          <div className="container-fluid">
            <ol className="clinic-breadcrumb-list d-flex">
              <li className="breadcrumb-item">
                <Link to="/" className="text-grey">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/doctor-list-page" className="text-grey">Doctors</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {doctor?.name || "Doctor Detail"}
              </li>
            </ol>
          </div>
        </div>

        {/* Main content */}
        <section className="doctor-detail pb-3 mt-3">
          <div className="container">
            <div className="row">
              <div className="filter_options">
                <div className="row">

                  {/* ── LEFT COLUMN ── */}
                  <div className="col-12 col-lg-6">
                    {/* Doctor Profile Card */}
                    <div className="dd-card">
                      <div className="dd-profile-row">
                        <div className="dd-profile-img">
                          <img
                            src={doctor?.images?.length
                              ? `${API_BASE_URL}public/doctorimages/${doctor.images.split(",")[0]}`
                              : `${API_BASE_URL}public/doctorimages/doctor-placeholder.jpg`}
                            alt={doctor?.name || "Doctor"}
                          />
                        </div>
                        <div className="dd-profile-info">
                          <h3 className="doctor-name">{doctor?.name}</h3>
                          <div className="dd-tags">
                            {doctor?.department_title?.name && (
                              <span className="dd-tag dd-tag-teal">
                                <i className="fa-solid fa-stethoscope"></i> {doctor.department_title.name}
                              </span>
                            )}
                            {doctor?.experience && (
                              <span className="dd-tag dd-tag-gold">
                                <i className="fa-solid fa-briefcase-medical"></i> {doctor.experience}
                              </span>
                            )}
                            <span className="dd-tag dd-tag-green">
                              <i className="fa-solid fa-circle-check"></i> Verified
                            </span>
                          </div>
                          {doctor?.specializations && (
                            <div className="dd-specialization">Specialization: {doctor.specializations}</div>
                          )}
                        </div>
                      </div>

                      {/* Description — Show More / Show Less */}
                      {doctor?.description && (
                        <div className="dd-desc-section">
                          <div className={`dd-desc-text ${descExpanded ? 'dd-expanded' : 'dd-clamped'}`}
                            dangerouslySetInnerHTML={{ __html: doctor.description }}
                          />
                          <button
                            className={`dd-toggle-btn ${descExpanded ? 'dd-open' : ''}`}
                            onClick={() => setDescExpanded(!descExpanded)}
                          >
                            {descExpanded ? 'Show Less' : 'Show More'}
                            <i className="fa-solid fa-chevron-down"></i>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Info Tabs — desktop */}
                    <div className="dd-tabs-card d-lg-block d-none">
                      <InfoTabs
                        doctor={doctor}
                        treatmentOptions={treatmentOptions}
                        renderDoctorReviewsPane={renderDoctorReviewsPane}
                        showHealthfeed={false}
                      />
                    </div>
                  </div>

                  {/* ── RIGHT COLUMN — Appointment ── */}
                  <div className="col-12 col-lg-6 tab-section-1">
                    <div className="dd-appt-sticky mb-3">

                      {/* Visit type tabs — Video first, Clinic second */}
                      <div className="dd-visit-type-tabs">
                        <ul className="nav nav-tabs radio-list" id="myTab" role="tablist">
                          <li className="nav-item" role="presentation">
                            <label id="profile-tab1" data-bs-toggle="tab" data-bs-target="#profile-tab-pane1"
                              className={`nav-link d-flex align-items-center gap-2 ${visitType === "video" ? "active" : ""}`}>
                              <input className="form-check-input mt-0" type="radio" name="exampleRadios"
                                value="video" checked={visitType === "video"}
                                onChange={(e) => setVisitType(e.target.value)} />
                              <span className="form-check-label">Video Consult</span>
                            </label>
                          </li>
                          <li className="nav-item" role="presentation">
                            <label id="home-tab1" data-bs-toggle="tab" data-bs-target="#home-tab-pane1"
                              className={`nav-link d-flex align-items-center gap-2 ${visitType === "In-person" ? "active" : ""}`}>
                              <input className="form-check-input mt-0" type="radio" name="exampleRadios"
                                value="In-person" checked={visitType === "In-person"}
                                onChange={(e) => setVisitType(e.target.value)} />
                              <span className="form-check-label">Clinic Visit</span>
                            </label>
                          </li>
                        </ul>
                      </div>

                      {/* Appointment content */}
                      <div className="dd-appt-body">
                        <div className="tab-content" id="myTabContent">
                          <div className={`tab-pane fade ${visitType === "video" ? "show active" : ""}`} id="profile-tab-pane1" role="tabpanel">
                            {renderAppointment("videocall_price", "Video Consultation", "fa-solid fa-video")}
                          </div>
                          <div className={`text-white tab-pane fade ${visitType === "In-person" ? "show active" : ""}`} id="home-tab-pane1" role="tabpanel">
                            {renderAppointment("consult_price", "Clinic Appointment", "fa-regular fa-house")}
                          </div>
                        </div>
                      </div>

                    </div>

                    {/* Info Tabs — mobile only */}
                    <div className="dd-tabs-card d-block d-lg-none">
                      <InfoTabs
                        doctor={doctor}
                        treatmentOptions={treatmentOptions}
                        renderDoctorReviewsPane={renderDoctorReviewsPane}
                        showHealthfeed={true}
                      />
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ── STICKY BOOK BAR (mobile) ── */}
      {doctor && (
        <div className="dd-sticky-bar">
          <div className="dd-sticky-left">
            <span className="dd-sticky-label">Consultation fee</span>
            <span className="dd-sticky-price  text-dark">₹ {currentFee || 0}</span>
          </div>
          <button className="dd-sticky-btn" onClick={handleCallNow}>
            <i className="fa-solid fa-calendar-check"></i> Book Appointment
          </button>
        </div>
      )}

      <Footer />
    </>
  );
};

export default FrontDoctorDetails;